import { escapeRegExp } from 'lodash-es';

import { type ColumnType } from '@blaze-shared/schema-domain';

import { type StyleToken } from './style-token';
import { type Token } from './token';
import { type TokenType } from './token-type';
import { type VariableToken } from './variable-token';

type VariableRenderHandler = (token: VariableToken) => string;

/** Handlebars segment-literal path to the raw row value. */
const variablePath = (token: VariableToken): string => `[${token.label}]`;

/**
 * Handlebars path expression per column type. Only types whose row value is an
 * object need an entry - every other type resolves the raw value directly.
 * Handlers must stay path expressions (not helper calls), as those are the only
 * form valid in every Handlebars position, block helper parameters included.
 */
const VARIABLE_RENDER_LUT: Partial<Record<ColumnType, VariableRenderHandler>> = {
  // DatasetLinkValue: { display, rowId }
  Link: (token) => `${variablePath(token)}.display`,
  // Signature: { fullName, signature, signedDate, userId }
  Signature: (token) => `${variablePath(token)}.fullName`,
};

const renderVariableToken: VariableRenderHandler = (token) => {
  const handler = token.dataType ? VARIABLE_RENDER_LUT[token.dataType] : undefined;

  return handler ? handler(token) : variablePath(token);
};

const SERIALIZATION_FOR_EDITOR_LUT: Record<TokenType, (token: any) => string> = {
  variable: (token: VariableToken) => `[$${token.type}(${token.value})]`,
  style: (token: StyleToken) => token.value,
};

const SERIALIZATION_FOR_RENDER_LUT: Record<TokenType, (token: any) => string> = {
  variable: renderVariableToken,
  style: (token: StyleToken) => token?.renderValue ?? token.value,
};

const TOKEN_MATCH_PATTERN_LUT: Record<TokenType, (token: any) => string> = {
  variable: (token: VariableToken) => `\\[\\$${token.type}\\(${escapeRegExp(token.value)}\\)\\]`,
  /**
   * Match a complete identifier so a token never replaces a fragment of a
   * longer one (`--theme-accent` must not match inside `--theme-accent-label`).
   * @param token
   */
  style: (token: StyleToken) => `(?<![-\\w])${escapeRegExp(token.value)}(?![-\\w])`,
};

export function serializeTokenForEditor(token: Token): string {
  return SERIALIZATION_FOR_EDITOR_LUT[token.type](token);
}

export function serializeTokenForRender(token: Token): string {
  return SERIALIZATION_FOR_RENDER_LUT[token.type](token);
}

export function replaceTokenForRender(code: string, token: Token): string {
  return replaceAllTokensForRender(code, [token]);
}

export function replaceAllTokensForRender(code: string, tokens: Token[]): string {
  const entries = tokens
    .filter((token) => token.value !== '')
    .map((token) => ({ token, pattern: TOKEN_MATCH_PATTERN_LUT[token.type](token) }))
    /**
     * Longer patterns first so a more specific token wins over one whose pattern
     * is a prefix of it (e.g. `--theme-accent-label` beats `--theme-accent`).
     */
    .sort((a, b) => b.pattern.length - a.pattern.length);

  if (entries.length === 0) {
    return code;
  }

  const combined = new RegExp(entries.map((entry) => `(${entry.pattern})`).join('|'), 'g');

  return code.replace(combined, (...args) => {
    const matchedGroups: (string | undefined)[] = args.slice(1, entries.length + 1);
    const matchedIndex = matchedGroups.findIndex((group) => group !== undefined);

    return serializeTokenForRender(entries[matchedIndex].token);
  });
}
